import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  LinearProgress,
  TextField,
  Alert,
  Chip,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import MicIcon from '@mui/icons-material/Mic';
import StopIcon from '@mui/icons-material/Stop';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import DeleteIcon from '@mui/icons-material/Delete';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import AudiotrackIcon from '@mui/icons-material/Audiotrack';
import lamejs from 'lamejs';


const AudioRecorder = () => {
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [fileName, setFileName] = useState('');
  const [description, setDescription] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState('');
  const [uploadError, setUploadError] = useState('');
  const [recordings, setRecordings] = useState([]);
  const [showRecordingsDialog, setShowRecordingsDialog] = useState(false);
  const [permissionStatus, setPermissionStatus] = useState('prompt');

  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerRef = useRef(null);
  const audioPlayerRef = useRef(null);
  const streamRef = useRef(null);

  const MAX_RECORDING_TIME = 600; // 10 minutes in seconds
const testMicrophone = async () => {
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    console.log('✅ Microphone works!');
    stream.getTracks().forEach(track => track.stop());
    setPermissionStatus('granted');
    alert('Microphone access granted! You can now record.');
  } catch (error) {
    console.error('❌ Microphone test failed:', error);
    setPermissionStatus('denied');
    alert('Microphone access denied. Please check browser settings.');
  }
};

  // ✅ Function to convert WebM to MP3 in browser
  const convertToMp3 = async (webmBlob) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = async (e) => {
        try {
          const arrayBuffer = e.target.result;
          const audioContext = new (window.AudioContext || window.webkitAudioContext)();
          const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
          
          // Get audio data
          const channels = audioBuffer.numberOfChannels;
          const sampleRate = audioBuffer.sampleRate;
          const samples = audioBuffer.getChannelData(0); // Get first channel
          
          // Convert Float32Array to Int16Array
          const buffer = new Int16Array(samples.length);
          for (let i = 0; i < samples.length; i++) {
            const s = Math.max(-1, Math.min(1, samples[i]));
            buffer[i] = s < 0 ? s * 0x8000 : s * 0x7FFF;
          }
          
          // Encode to MP3
          const mp3encoder = new lamejs.Mp3Encoder(1, sampleRate, 192); // 1 channel, sampleRate, 192kbps
          const mp3Data = [];
          const sampleBlockSize = 1152;
          
          for (let i = 0; i < buffer.length; i += sampleBlockSize) {
            const sampleChunk = buffer.subarray(i, i + sampleBlockSize);
            const mp3buf = mp3encoder.encodeBuffer(sampleChunk);
            if (mp3buf.length > 0) {
              mp3Data.push(mp3buf);
            }
          }
          
          // Flush remaining data
          const mp3buf = mp3encoder.flush();
          if (mp3buf.length > 0) {
            mp3Data.push(mp3buf);
          }
          
          // Create MP3 blob
          const mp3Blob = new Blob(mp3Data, { type: 'audio/mp3' });
          
          console.log('✅ Converted to MP3');
          console.log('   Original size:', webmBlob.size, 'bytes');
          console.log('   MP3 size:', mp3Blob.size, 'bytes');
          console.log('   Compression:', ((1 - mp3Blob.size / webmBlob.size) * 100).toFixed(1) + '%');
          
          resolve(mp3Blob);
        } catch (error) {
          reject(error);
        }
      };
      
      reader.onerror = reject;
      reader.readAsArrayBuffer(webmBlob);
    });
  };

  const startRecording = async () => {
    try {
      setUploadSuccess('');
      setUploadError('');
      
      console.log('🎤 Requesting microphone access...');
      
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 44100
        } 
      });
      
      console.log('✅ Microphone access granted');
      setPermissionStatus('granted');
      streamRef.current = stream;

      const options = { mimeType: 'audio/webm;codecs=opus' };
      const mediaRecorder = new MediaRecorder(stream, options);
      mediaRecorderRef.current = mediaRecorder;

      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
          console.log('📦 Audio chunk received:', event.data.size, 'bytes');
        }
      };

        mediaRecorder.onstop = () => {
        console.log('⏹️ Recording stopped');
        const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        
        setAudioBlob(blob);
        setAudioUrl(URL.createObjectURL(blob));
        
        // Generate MP3 filename
        const timestamp = new Date().toISOString().replace(/[:.]/g, '-').substring(0, 19);
        setFileName(`recording-${timestamp}.mp3`);  // ✅ .mp3 extension
        };

      mediaRecorder.start(1000);
      setIsRecording(true);
      setRecordingTime(0);

      timerRef.current = setInterval(() => {
        setRecordingTime(prev => {
          const newTime = prev + 1;
          if (newTime >= MAX_RECORDING_TIME) {
            stopRecording();
            return MAX_RECORDING_TIME;
          }
          return newTime;
        });
      }, 1000);

    } catch (error) {
      console.error('❌ Error accessing microphone:', error);
      
      if (error.name === 'NotAllowedError') {
        setPermissionStatus('denied');
        setUploadError(
          'Microphone access denied. Please allow microphone access in your browser settings and reload the page.'
        );
      } else if (error.name === 'NotFoundError') {
        setUploadError('No microphone found. Please connect a microphone and try again.');
      } else if (error.name === 'NotReadableError') {
        setUploadError('Microphone is already in use by another application.');
      } else {
        setUploadError(`Could not access microphone: ${error.message}`);
      }
    }
  };

  const handleUpload = async () => {
    if (!audioBlob) {
      setUploadError('No recording to upload');
      return;
    }

    if (!fileName.trim()) {
      setUploadError('Please enter a file name');
      return;
    }

    // ✅ Ensure .mp3 extension
    let finalFileName = fileName.trim();
    if (!finalFileName.toLowerCase().endsWith('.mp3')) {
      finalFileName = finalFileName.replace(/\.(webm|wav|ogg)$/i, '') + '.mp3';
    }

    setUploading(true);
    setUploadSuccess('');
    setUploadError('');

    try {
      const formData = new FormData();
      formData.append('audioFile', audioBlob, finalFileName);
      formData.append('fileName', finalFileName);
      formData.append('description', description);

      console.log('⬆️ Uploading MP3 recording:', finalFileName);
      console.log('   Size:', audioBlob.size, 'bytes');

      const response = await fetch('/api/audio/record/upload', {
        method: 'POST',
        body: formData
      });

      const data = await response.json();

      if (data.success) {
        setUploadSuccess(`✅ Recording uploaded successfully as MP3! Available at: ${data.file.name}`);
        
        // Reset form
        setAudioBlob(null);
        setAudioUrl(null);
        setFileName('');
        setDescription('');
        setRecordingTime(0);
        
        // Optionally refresh the audio list if on AudioMix page
        // fetchRecordings();
      } else {
        setUploadError(data.message || 'Upload failed');
      }
    } catch (error) {
      console.error('Upload error:', error);
      setUploadError('Error uploading recording: ' + error.message);
    } finally {
      setUploading(false);
    }
  };

  useEffect(() => {
    return () => {
      // Cleanup
      if (timerRef.current) clearInterval(timerRef.current);
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    
    setIsRecording(false);
  };

  const togglePlayback = () => {
    if (!audioPlayerRef.current) return;

    if (isPlaying) {
      audioPlayerRef.current.pause();
    } else {
      audioPlayerRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };


  const handleDiscard = () => {
    setAudioBlob(null);
    setAudioUrl(null);
    setFileName('');
    setDescription('');
    setRecordingTime(0);
    setIsPlaying(false);
  };

  const fetchRecordings = async () => {
    try {
      const response = await fetch('/api/audio/recordings');
      const data = await response.json();
      
      if (data.success) {
        setRecordings(data.recordings || []);
      }
    } catch (error) {
      console.error('Error fetching recordings:', error);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const progress = (recordingTime / MAX_RECORDING_TIME) * 100;
  const timeRemaining = MAX_RECORDING_TIME - recordingTime;

  return (
    <Paper elevation={3} sx={{ p: 3, maxWidth: 800, margin: '0 auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <AudiotrackIcon sx={{ fontSize: 40, mr: 2, color: 'primary.main' }} />
        <Typography variant="h5">
          <strong>Audio Recorder</strong>
        </Typography>
      </Box>

      {uploadSuccess && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setUploadSuccess('')}>
          {uploadSuccess}
        </Alert>
      )}

      {uploadError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setUploadError('')}>
          {uploadError}
        </Alert>
      )}

<Button
  variant="outlined"
  onClick={testMicrophone}
  sx={{ mb: 2 }}
>
  Test Microphone
</Button>

      {/* Recording Controls */}
      <Box sx={{ textAlign: 'center', mb: 3 }}>
        {!isRecording && !audioBlob && (
          <Button
            variant="contained"
            color="error"
            size="large"
            startIcon={<MicIcon />}
            onClick={startRecording}
            sx={{ px: 4, py: 2 }}
          >
            Start Recording
          </Button>
        )}

        {isRecording && (
          <Box>
            <Box sx={{ mb: 2 }}>
              <Chip
                label="RECORDING"
                color="error"
                sx={{ 
                  fontSize: '1.2rem', 
                  px: 2, 
                  py: 3,
                  animation: 'pulse 1.5s ease-in-out infinite',
                  '@keyframes pulse': {
                    '0%, 100%': { opacity: 1 },
                    '50%': { opacity: 0.5 }
                  }
                }}
              />
            </Box>
            
            <Typography variant="h3" sx={{ mb: 2, fontFamily: 'monospace' }}>
              {formatTime(recordingTime)}
            </Typography>
            
            <LinearProgress 
              variant="determinate" 
              value={progress} 
              sx={{ mb: 2, height: 8, borderRadius: 4 }}
            />
            
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Time remaining: {formatTime(timeRemaining)} (Max: 10 minutes)
            </Typography>
            
            <Button
              variant="contained"
              color="primary"
              size="large"
              startIcon={<StopIcon />}
              onClick={stopRecording}
              sx={{ px: 4, py: 2 }}
            >
              Stop Recording
            </Button>
          </Box>
        )}

        {audioBlob && !isRecording && (
          <Box>
            <Alert severity="info" sx={{ mb: 2 }}>
              Recording complete! Duration: {formatTime(recordingTime)}
            </Alert>

            {/* Audio Player */}
            <Box sx={{ mb: 3 }}>
              <audio
                ref={audioPlayerRef}
                src={audioUrl}
                onEnded={() => setIsPlaying(false)}
                style={{ display: 'none' }}
              />
              
              <Button
                variant="outlined"
                startIcon={isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
                onClick={togglePlayback}
                sx={{ mr: 2 }}
              >
                {isPlaying ? 'Pause' : 'Play'} Preview
              </Button>
              
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={handleDiscard}
              >
                Discard
              </Button>
            </Box>

            {/* Upload Form */}
            <Box sx={{ textAlign: 'left' }}>
              <TextField
                fullWidth
                label="File Name"
                value={fileName}
                onChange={(e) => setFileName(e.target.value)}
                placeholder="recording-2024-01-01.webm"
                required
                margin="normal"
                helperText="Will be converted to MP3 format"
              />

              <TextField
                fullWidth
                label="Description (Optional)"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Describe this recording..."
                multiline
                rows={2}
                margin="normal"
              />

              <Box sx={{ mt: 2, mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  File size: {audioBlob ? formatFileSize(audioBlob.size) : '0 KB'}
                </Typography>
              </Box>

              <Button
                fullWidth
                variant="contained"
                color="success"
                size="large"
                startIcon={<CloudUploadIcon />}
                onClick={handleUpload}
                disabled={uploading || !fileName.trim()}
                sx={{ py: 1.5 }}
              >
                {uploading ? 'Uploading to GitHub...' : 'Upload to Audio Mix Folder'}
              </Button>
            </Box>
          </Box>
        )}
      </Box>

      {/* View Recordings Button */}
      <Box sx={{ textAlign: 'center', mt: 3 }}>
        <Button
          variant="outlined"
          onClick={() => {
            fetchRecordings();
            setShowRecordingsDialog(true);
          }}
        >
          View All Recordings
        </Button>
      </Box>

      {/* Recordings Dialog */}
      <Dialog
        open={showRecordingsDialog}
        onClose={() => setShowRecordingsDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Audio Recordings in GitHub
        </DialogTitle>
        <DialogContent>
          {recordings.length > 0 ? (
            <List>
              {recordings.map((recording, index) => (
                <ListItem
                  key={index}
                  secondaryAction={
                    <Box>
                      <IconButton
                        edge="end"
                        href={recording.url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <PlayArrowIcon />
                      </IconButton>
                    </Box>
                  }
                >
                  <ListItemText
                    primary={recording.name}
                    secondary={`Size: ${formatFileSize(recording.size)}`}
                  />
                </ListItem>
              ))}
            </List>
          ) : (
            <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
              No recordings found
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowRecordingsDialog(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Instructions */}
      <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
        <Typography variant="subtitle2" gutterBottom>
          📝 Instructions:
        </Typography>
        <Typography variant="body2" component="ul" sx={{ pl: 2, mb: 0 }}>
          <li>Click "Start Recording" to begin</li>
          <li>Speak or record audio for up to 10 minutes</li>
          <li>Click "Stop Recording" when finished</li>
          <li>Preview your recording before uploading</li>
          <li>Add a file name and description</li>
          <li>Click "Upload" to save to GitHub Drive</li>
          <li>Recordings will be available in the Audio Mix folder</li>
        </Typography>
      </Box>
    </Paper>
  );
};

export default AudioRecorder;